import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import API from '../../../api/API';
import './QRPlanning.css';

import DesktopPlanningView from './components/DesktopPlanningView';
import MobilePlanningView from './components/MobilePlanningView';
import TeamFilter from './components/TeamFilter';
import { raggruppaTurniPerFase } from '../../../components/planning/fasiPlanning';
import { TURNO_PANCHINA } from '../../../components/planning/panchina';

const QRPlanning = () => {
  const { id: torneoId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxTurno, setMaxTurno] = useState(0);
  // Confini delle bande delle fasi spostati a mano dall'admin: qui si leggono e basta.
  const [confiniFasi, setConfiniFasi] = useState({});
  const [maxCampo, setMaxCampo] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedTeam, setSelectedTeam] = useState(searchParams.get('team') || '');
  const [availableTeams, setAvailableTeams] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (torneoId) {
      loadPlanning(torneoId);
    } else {
      setLoading(false);
      setError('ID torneo non trovato nei parametri URL');
    }
  }, [torneoId]);

  const loadPlanning = async (idTorneo) => {
    try {
      setLoading(true);
      console.log('Loading planning for torneo ID:', idTorneo);
      
      const response = await API.planning.public(idTorneo);
      console.log('API Response:', response);

      // I confini non bloccano il planning: se non arrivano si disegnano le bande calcolate.
      API.planning.confiniFasi(idTorneo).then(r => {
        if (r.isSuccess && Array.isArray(r.data)) {
          setConfiniFasi(Object.fromEntries(r.data.map(c => [c.fase, c.primoTurno])));
        }
      }).catch(() => {});

      if (response.isSuccess && response.data) {
        // Le partite in panchina (turno 0, tolte dal calendario dall'admin) non sono in programma:
        // qui non si mostrano, o disegnerebbero un "Turno 0" fantasma.
        const planningData = response.data.filter(m => m.turno !== TURNO_PANCHINA);
        console.log('Planning data:', planningData);
        setMatches(planningData);
        
        // Calculate max turno and campo
        const maxT = planningData.length ? Math.max(...planningData.map(m => m.turno)) : 0;
        const maxC = planningData.length ? Math.max(...planningData.map(m => m.campo)) : 0;
        setMaxTurno(maxT);
        setMaxCampo(maxC);

        // Extract unique teams
        const teams = new Set();
        planningData.forEach(match => {
          if(match.s1Id > 0)
            teams.add(match.squadra1);

          if(match.s2Id > 0)
            teams.add(match.squadra2);
          
          if (match.arbitro || match.squadraA) {
            teams.add(match.arbitro || match.squadraA);
          }
        });
        setAvailableTeams(Array.from(teams).sort());
      } else {
        console.log('API response error:', response);
        setError(`Errore nel caricamento del planning: ${response.error || 'Risposta non valida'}`);
      }
    } catch (err) {
      console.error('Error loading planning:', err);
      setError(`Errore durante il caricamento del planning: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getMatchAtPosition = (turno, campo) => {
    return matches.find(m => m.turno === turno && m.campo === campo);
  };

  const getMatchWinner = (match) => {
    if (!match.risultato) return null;
    
    const { mete1, mete2 } = match.risultato;
    
    if (mete1 > mete2) return '1';
    if (mete1 < mete2) return '2';
    return 'X'; // Pareggio
  };

  /**
   * Il colore che dice a quale gruppo appartiene la partita.
   *
   * Si legge da `nome`, cioe' il nome del girone o del tabellone dentro la formula. PPartita
   * portava un campo `girone` e qui si leggeva quello: quando il record e' stato rifatto con
   * `tipo` piu' `nome` questa pagina non e' stata aggiornata, e da allora arrivava undefined a
   * split(). L'eccezione smontava l'albero React, quindi pagina bianca - subito sul desktop, che
   * disegna tutte le partite in griglia, e sul telefono appena si apriva un turno o si filtrava
   * per squadra. La guardia sul nome mancante e' la stessa di APlanning.getMatchColor, che il
   * cambio di payload lo aveva assorbito.
   */
  const getMatchColor = (match) => {
    const nome = match?.nome;
    if (!nome) return 'linear-gradient(135deg, #6c757d 0%, #495057 100%)';

    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Blu-Viola
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Rosa-Rosso
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Azzurro-Ciano
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Verde-Turchese
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Rosa-Giallo
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Acqua-Rosa
      'linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%)', // Giallo-Pesca
      'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)', // Blu chiaro-scuro
      'linear-gradient(135deg, #fd79a8 0%, #fdcb6e 100%)', // Rosa-Arancione
      'linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%)', // Viola scuro-chiaro
      'linear-gradient(135deg, #00b894 0%, #00cec9 100%)', // Verde-Turchese
      'linear-gradient(135deg, #e17055 0%, #f39c12 100%)', // Arancione-Giallo
      'linear-gradient(135deg, #81ecec 0%, #74b9ff 100%)', // Ciano-Blu
      'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)', // Rosa chiaro-scuro
      'linear-gradient(135deg, #55a3ff 0%, #003d82 100%)', // Blu elettrico
      'linear-gradient(135deg, #ff7675 0%, #d63031 100%)', // Rosso chiaro-scuro
      'linear-gradient(135deg, #00b894 0%, #55efc4 100%)', // Verde acqua
      'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)', // Lilla-Viola
      'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)', // Giallo-Arancio
      'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)', // Azzurro
    ];
    
    // L'hash del nome tiene il colore stabile fra un caricamento e l'altro
    const hash = nome.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getFilteredMatches = () => {
    if (!selectedTeam) {
      return matches;
    }
    
    return matches.filter(match => 
      match.squadra1 === selectedTeam || 
      match.squadra2 === selectedTeam ||
      match.arbitro === selectedTeam ||
      match.squadraA === selectedTeam
    );
  };

  const handleTeamChange = (team) => {
    setSelectedTeam(team);
    if (team) {
      setSearchParams({ team });
    } else {
      setSearchParams({});
    }
  };

  const getGroupedFilteredMatches = () => {
    const filtered = getFilteredMatches();
    const grouped = new Map();
    
    filtered.forEach(match => {
      const turno = match.turno;
      if (!grouped.has(turno)) {
        grouped.set(turno, []);
      }
      grouped.get(turno).push(match);
    });
    
    // Convert to sorted array
    const sortedGroups = [];
    grouped.forEach((matches, turno) => {
      matches.sort((a, b) => a.campo - b.campo);
      sortedGroups.push({
        turno,
        matches
      });
    });
    
    return sortedGroups.sort((a, b) => a.turno - b.turno);
  };

  if (loading) {
    return (
      <div className="qrp-planning-page">
        <div className="qrp-planning-loading">
          <div className="qrp-loading-spinner"></div>
          <p>Caricamento planning...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qrp-planning-page">
        <div className="qrp-planning-error">
          <h3>Errore</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const filteredMatches = getFilteredMatches();
  const groupedFilteredMatches = getGroupedFilteredMatches();
  const fasiRaggruppate = raggruppaTurniPerFase(matches, maxTurno, confiniFasi);

  return (
    <div className="qrp-planning-page">
      <div className="qrp-planning-header">
        <h1>Planning Partite</h1>
        <TeamFilter
          availableTeams={availableTeams}
          selectedTeam={selectedTeam}
          onTeamChange={handleTeamChange}
        />
      </div>

      {selectedTeam ? (
        <div className="qrp-team-matches-list">
          {groupedFilteredMatches.map((turnoGroup, groupIndex) => (
            <div key={groupIndex} className="qrp-turno-group">
              <div className="qrp-turno-matches">
                {turnoGroup.matches.map((match, index) => (
                  <div 
                    key={index}
                    className="qrp-mobile-match-card"
                    style={{ 
                      background: getMatchColor(match),
                      border: match.risultato ? '2px solid #333' : 'none'
                    }}
                  >
                    <div className="qrp-match-header-mobile">
                       <span className="qrp-match-campo">
                        Turno {match.turno}
                      </span>
                      <span className="qrp-match-campo">
                        Campo {match.campo}
                      </span>
                      <span className="qrp-match-girone">{match.nome}</span>
                      {getMatchWinner(match) && (
                        <div className="qrp-match-winner-badge">
                          {getMatchWinner(match)}
                        </div>
                      )}
                    </div>
                    
                    <div className="qrp-match-teams-mobile">
                      <div className="qrp-teams-single-row">
                        <div className="qrp-team-name-left">
                          {match.squadra1}
                        </div>
                        <div className="qrp-score-center">
                          {match.risultato ? 
                            `${match.risultato.mete1}-${match.risultato.pieni1}-${match.risultato.vuoti1} vs ${match.risultato.mete2}-${match.risultato.pieni2}-${match.risultato.vuoti2}` : 
                            ' '
                          }
                        </div>
                        <div className="qrp-team-name-right">
                          {match.squadra2}
                        </div>
                      </div>
                      {(match.arbitro === selectedTeam || match.squadraA === selectedTeam) && (
                        <div className="qrp-referee-badge">
                          Arbitro
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {isMobile ? (
            <MobilePlanningView
              matches={matches}
              maxTurno={maxTurno}
              maxCampo={maxCampo}
              getMatchAtPosition={getMatchAtPosition}
              getMatchWinner={getMatchWinner}
              getMatchColor={getMatchColor}
              fasiRaggruppate={fasiRaggruppate}
            />
          ) : (
            <DesktopPlanningView
              matches={matches}
              maxTurno={maxTurno}
              maxCampo={maxCampo}
              getMatchAtPosition={getMatchAtPosition}
              getMatchWinner={getMatchWinner}
              getMatchColor={getMatchColor}
              fasiRaggruppate={fasiRaggruppate}
            />
          )}
        </>
      )}
    </div>
  );
};

export default QRPlanning;